import { useState, createContext, useEffect, PropsWithChildren } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, AlertColor, Slide, SlideProps, Snackbar } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import i18n from 'i18next';
import { setDateLocale } from '../lib/dateLocale';
import ModalConfirmation from '../layout/ModalConfirmation';
import { useAPI, useLoader, useSocketConnection } from '../lib/hooks';
import { loadConstants } from '../constants';
import {
    APPPROVIDER_INFO,
    APPPROVIDER_MANAGER_ME,
    APPPROVIDER_ME,
    APPPROVIDER_TRANSLATIONS,
    APPPROVIDER_V7MIGRATIONENDPOINTS,
} from '../lib/queryKeys';
import generalConfig from '../config/general';
import { readLocaleCookie, writeLocaleCookie } from '../lib/localeCookie';
import { getErrorMessage } from '../lib/utils';
import { SocketMessage, isObject } from '../types/common';
import {
    Licence,
    Sportcenter as BaseSportcenter,
    User,
} from '../types/api/models';
import { GetUser200 } from '../types/api/paths/User';
import { APIEnv } from '../types/config';
import menus from '../config/menus.json';
import { HttpVerb, MigrationApis } from '../types/api/utils';
import { GetMigrations200 } from '../types/api/paths/Migration';
import { GetInfo200 } from '../types/api/paths/Info';
import configConstants from '../config/constants';
import {
    Flag,
    flagsQueryOptions,
    isFlagActive,
} from '../services/FlagsService';

/**
 * Respuesta de `GET /v8/manager/me`: sólo se lee lo que usa este gestor. Como
 * el resto del v8, viene envuelta en `data`.
 */
type ManagerMe = { data?: { permissions?: string[] } };

const NO_PERMISSIONS: string[] = [];

// Extended Sportcenter type with additional properties not in the API schema
type Sportcenter = BaseSportcenter & {
    logo?: string;
    TPVFeePayment?: boolean;
    cashdroEnabled?: boolean;
    stripeEnabled?: boolean;
    /** Ajuste por centro de Sporttia Copilot; `null` si el centro no tiene valor. */
    copilotEnabled?: boolean | null;
    [key: string]: unknown;
};

// Slide transition for Snackbar (from top)
function SlideTransition(props: SlideProps) {
    return <Slide {...props} direction="down" />;
}

// Get icon based on alert type
const getAlertIcon = (type: AlertColor) => {
    switch (type) {
        case 'success':
            return <CheckCircleOutlineIcon />;
        case 'error':
            return <ErrorOutlineIcon />;
        case 'warning':
            return <WarningAmberIcon />;
        default:
            return <InfoOutlinedIcon />;
    }
};

const preserveLocalStorageValues = [
    'version',
    'environment',
    'lang',
    // Preferencias de interfaz compartidas con sporttia-competitions: si el
    // logout las borrara, cerrar sesión aquí cambiaría el aspecto de la otra
    // aplicación.
    'theme',
    'sporttia-content-width',
    'registeredDevices',
    'CM5DeviceAddress',
    'CashdroIp',
    'CashdroUser',
    'CashdroPass',
    'sporttia_menus_tour_completed',
];

type ApiOptions<TData = unknown> = {
    params?: Record<string, unknown>;
    success?: (value: TData) => void;
    error?: (value: unknown) => void;
    loading?: (value: boolean) => void;
    confirmation?: boolean;
    version?: string;
};

type AlertType = 'I' | 'W' | 'E' | 'S' | undefined;

type Language = (typeof generalConfig)['languages'][number];

type Constants = ReturnType<typeof loadConstants>;

type Header = {
    title: string;
};

type IAppContext = {
    user?: User;
    privileges?: number[];
    sc?: Sportcenter;
    licence?: Licence;
    config: typeof generalConfig;
    menus: typeof menus;
    apiEnv: APIEnv;
    backendVersion?: string;
    curtesyMinutesForTPV?: number;
    /**
     * @deprecated Utilizar config/constants
     */
    constants: Constants;
    setLang: React.Dispatch<React.SetStateAction<Language>>;
    lang: Language;
    /**
     * @deprecated Utilizar react-i18next useTranslation hook
     */
    t: (translationKey: string) => string;
    /**
     * @deprecated Utilizar services
     */
    api: <TData = unknown>(
        method: HttpVerb,
        resource: string,
        params?: ApiOptions<TData>,
    ) => void;
    getResource: (path: string, method?: HttpVerb) => string;
    migrationApis?: MigrationApis | null;
    showMessage: (type: AlertType, text: string, duration?: number) => void;
    logged: boolean;
    setLoggedUser: (user: User | false) => void;
    header: Header | null;
    setHeader: React.Dispatch<React.SetStateAction<Header | null>>;
    getSocketMessages: () => SocketMessage[];
    deleteReceivedMessage: (message: SocketMessage) => void;
    sendMessageToSocket: (message: SocketMessage) => void;
    drawerOpen: boolean;
    setDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
    zero: boolean;
    setZero: React.Dispatch<React.SetStateAction<boolean>>;
    /**
     * Si los roles del manager nuevo mandan en esta sesión: flag
     * `manager-roles-permissions` encendido y operador de centro. Apagado, el
     * gestor se comporta como antes de los roles (`can()` siempre `true`).
     */
    rolesEnabled: boolean;
    /**
     * Códigos de permiso v8 del operador (`GET /v8/manager/me`), por ejemplo
     * `'booking.create'`. Vacío con los roles apagados. Para comprobarlos usa
     * `usePermission()` o `can()` de `lib/permissions`.
     */
    permissions: string[];
};

export const AppContext = createContext<IAppContext | undefined>(undefined);

/**
 * Returns the object with the language filtering by its key.
 */
const getLanguageByKey = (languageKey: string | undefined) =>
    generalConfig.languages.find((e: any) => e.key === languageKey) ||
    generalConfig.languages.find((e: any) => e.key === 'en')!;

const isSupportedLanguage = (languageKey: string): boolean =>
    generalConfig.languages.some((e: any) => e.key === languageKey);

/**
 * Gets the user's language key:
 *      - If it is the first time you log in, the language that
 *      the user has in the browser is used (if it exists in Sporttia)
 *      and if the English language is not used.
 *      - If the user has already entered for the first time, the language
 *      that the user has configured in their profile is used.
 */
const getUserLanguage = () => {
    // La cookie `locale` la comparte sporttia-competitions, que se sirve desde
    // el mismo dominio: si trae un idioma que aquí existe manda sobre el último
    // guardado, así el cambio hecho allí se nota al pasar. Se sigue comprobando
    // que exista (aunque ahora las dos ofrezcan los mismos ocho idiomas) porque
    // getLanguageByKey caería a inglés ante una clave desconocida.
    const sharedLocale = readLocaleCookie();
    if (sharedLocale && isSupportedLanguage(sharedLocale)) {
        return getLanguageByKey(sharedLocale);
    }

    const storedLanguage = localStorage.getItem('lang');
    const navigatorLanguage = navigator.language.split('-')[0];

    return storedLanguage
        ? getLanguageByKey(storedLanguage)
        : getLanguageByKey(navigatorLanguage);
};

/**
 * Clean local storage, remove all values except for keys declared in 'preserveLocalStorageValues'
 */
const cleanLocalStorage = () => {
    Object.keys(localStorage).forEach((localStorageKey: any) => {
        if (
            localStorageKey in localStorage &&
            !preserveLocalStorageValues.includes(localStorageKey)
        ) {
            localStorage.removeItem(localStorageKey);
        }
    });
};

/**
 * Data provider to pass data through the entire component tree of the project. This provider is divided into six parts:
 * ApiDocV7, Translations, AlertsUI, Api, CommonData, Authentication
 * and App try not to divide these parts to be able to maintain it in the future correctly.
 */
export function AppProvider({ children }: PropsWithChildren<unknown>) {
    // establecer las funciones de conexión y mensaje por socket
    const [
        openSocket,
        closeSocket,
        receivedMessages,
        sendMessage,
        deleteReceivedMessage,
    ] = useSocketConnection();

    // ApiDocV7 - Aquello relacionado con la integración de v7, esto en el futuro debería eliminarse cuando ya no se use v6.
    // Actualmente lo que se realiza es una petición a https://preapi.sporttia.com:8443/v7/api/doc.json para recuperar todas los endpoints
    // que tenemos funcionando en v7 para poder ser usados, se setea "migrationApis" para que el método api() lo utilice como forma de elegir
    // si un end point se pide con v6 o con v7.
    const [request, migrationApis, setMigrationApis, getResource] = useAPI();
    const {
        data: v7DocData,
        status: v7DocInfoStatus,
        isError: v7DocIsError,
    } = useQuery<GetMigrations200>({
        queryKey: [APPPROVIDER_V7MIGRATIONENDPOINTS],
        queryFn: () =>
            request<GetMigrations200>('GET', '/migration-endpoints', {
                active: true,
            }),
    });

    // Handle v7Doc data changes
    useEffect(() => {
        if (v7DocData) {
            setMigrationApis(v7DocData?.rows || {});
        } else if (v7DocIsError) {
            setMigrationApis([]);
        }
    }, [v7DocData, v7DocIsError]);

    const { data: infoData, status: infoDataStatus } = useQuery({
        queryKey: [APPPROVIDER_INFO],
        queryFn: () => request<GetInfo200>('GET', '/info'),
        enabled: migrationApis !== null && v7DocInfoStatus === 'success',
    });

    // Handle infoData changes
    useEffect(() => {
        if (infoData) {
            localStorage.setItem('environment', infoData.environment);
        }
    }, [infoData]);

    // Translations - Language configuration
    const [language, setLanguage] = useState(getUserLanguage());

    // Translations are now handled by i18n (react-i18next) with local JSON files
    // This query only provides the disabledApi translations for backward compatibility
    const { data: translations, status: translationsStatus } = useQuery({
        queryKey: [APPPROVIDER_TRANSLATIONS, { lang: language.key }],
        queryFn: () => {
            localStorage.setItem('lang', language.key);
            return {
                'disabledapi.message':
                    configConstants.disabledApi.message[language.key],
                'disabledapi.title':
                    configConstants.disabledApi.title[language.key],
                'disabledapi.reload':
                    configConstants.disabledApi.reload[language.key],
            };
        },
        enabled: !!infoData,
        initialData: () => ({
            'disabledapi.message':
                configConstants.disabledApi.message[language.key],
            'disabledapi.title':
                configConstants.disabledApi.title[language.key],
            'disabledapi.reload':
                configConstants.disabledApi.reload[language.key],
        }),
    });

    /**
     * Get translation using translation key.
     * @deprecated Use react-i18next useTranslation hook instead
     */
    const translate = (translationKey: string) => {
        const translationKeyLowerCase = translationKey
            ? translationKey.toLowerCase()
            : '';

        const translationsRecord = translations as Record<
            string,
            string | undefined
        >;
        if (
            translationsRecord &&
            translationKeyLowerCase in translationsRecord
        ) {
            return (
                translationsRecord[translationKeyLowerCase] ||
                `[[${translationKeyLowerCase}]]`
            );
        }
        return `[[${translationKeyLowerCase}]]`;
    };

    // AlertsUI - Aquello relacionado con el dialogo de confirmación que usa api() y los mensajes de error que aparecen como un popup en la web.
    // Existen dos componentes en Sporttia genericos un dialogo para confirmar las operaciones de la API y otro componente para mostrar alertas con mensajes
    // de error, warnings o mensajes de éxito (https://mui.com/components/alert/).
    const [confirmationDialog, setConfirmationDialog] = useState<{
        show: boolean;
        onAccept?: () => void;
    }>({
        show: false,
    });
    const [msg, setMsg] = useState<{
        type: AlertColor;
        text: string;
        duration: number;
    } | null>(null);

    /**
     * Show an alert in the app.
     */
    const showMessage = (
        type: AlertType = 'I',
        text: string,
        duration = 5000,
    ) => {
        if (!text) {
            return;
        }

        let messageType: AlertColor;
        switch (type) {
            case 'I':
                messageType = 'info';
                break;
            case 'W':
                messageType = 'warning';
                break;
            case 'E':
                messageType = 'error';
                break;
            case 'S':
                messageType = 'success';
                break;
            default:
                messageType = 'info';
        }

        setMsg({
            type: messageType,
            text,
            duration,
        });
    };

    // CommonData - Aquello relacionado con información común de la aplicación (el /info y el /me básicamente).
    // Cuando se abre por primera vez Sporttia se hace una petición a /info para obtener información del back-end y
    // cuando el usuario se conecta se piden datos a /me para obtener datos de su cuenta, con estos datos se setean
    // diferentes valores en el localStorage y son usados en la aplicación para realizar comprobaciones.
    // PD: He visto que los datos usado a través de localStorage en toda la APP solo son: lang, token, environment, idSc
    // luego he eliminado todos los que se seteaban antes que no se usan ahora.
    const [constants, setConstants] = useState(loadConstants(i18n.t));
    const [userData, setUserData] = useState<GetUser200>({
        user: {
            id: undefined,
            role: undefined,
        },
    });
    const [userLogged, setUserLogged] = useState(
        !window.location.pathname.startsWith('/impersonate'),
    );

    // Api - Aquello relacionado con los métodos para llamar a la api.
    // Método usado en Sporttia para hacer peticiones a la API, en el futuro esto debería eliminarse y usar en su caso
    // lo nuevos hooks de servicios (useCitiesService(), useUsersService()...)
    /**
     * Helper method to api (), is where all the work of api () is done.
     * @private
     */
    const apiHelper = <TData,>(
        method: HttpVerb,
        resource: string,
        p: ApiOptions<TData> = {},
    ) => {
        if (p.loading) {
            p.loading(true);
        }

        request(method, resource, p.params || {}, p.version)
            .then((response: any) => {
                if (p.confirmation) {
                    setConfirmationDialog({ show: false });
                }

                // request() hace reject en caso de "error 200", de forma que aquí solo puede haber 'success'
                if (p.success) {
                    p.success(response as TData);
                }
            })
            .catch((error: any) => {
                if (error !== constants.apiLock) {
                    if (p.error) {
                        p.error(error);
                    } else {
                        showMessage('E', getErrorMessage(error));
                    }
                }
            })
            .finally(() => {
                if (p.loading) {
                    p.loading(false);
                }
            });
    };

    /**
     * Method for making API calls.
     * @param method HTTP Verb (GET | POST | PUT | DELETE).
     * @param resource Resource to be requested (/resource).
     * @param p Request params.
     */
    const api = <TData,>(
        method: HttpVerb,
        resource: string,
        p: ApiOptions<TData> = { confirmation: false },
    ) => {
        if (p.confirmation) {
            setConfirmationDialog({
                show: true,
                onAccept: () => apiHelper(method, resource, p),
            });
        } else {
            apiHelper(method, resource, p);
        }
    };

    // Authentication - Aquello relacionado con la autenticación del usuario, básicamente limpieza de datos al
    // hacer logout y seteo de token al iniciar sesión.//
    /**
     * If the value of "user" is not undefined, the user has logged in and the token and the value of "userLogged" are set.
     * On the other hand, if the value of "user" is undefined, the localStorage and the values of "userLogged", "userToken"
     * and "userData" are cleaned (it is left with user: {id: value, role: value} because if it does not fail the topic of
     * headers and so on.).
     * @param user Object with user value from GET /login request.
     */
    const setLoggedUser = (user: User | false) => {
        if (user) {
            // login, básicamente
            setUserLogged(true);
        } else {
            // logout, básicamente
            cleanLocalStorage();
            localStorage.removeItem('impersonateStartTime');
            setUserLogged(false);
            setUserData({
                user: {
                    id: undefined,
                    role: undefined,
                },
            });
        }
    };

    const {
        data: loggedUserData,
        status: loggedUserDataStatus,
        error: loggedUserError,
    } = useQuery({
        queryKey: [APPPROVIDER_ME],
        queryFn: () => request<GetUser200>('GET', '/me'),
        enabled:
            migrationApis !== null &&
            userLogged &&
            v7DocInfoStatus === 'success',
    });

    // Handle logged user data changes
    useEffect(() => {
        if (loggedUserData) {
            function addFieldToModules(sc: Sportcenter) {
                if (!sc.modules) {
                    return;
                }

                const updatedScModules = [...sc.modules];
                if (sc.dataphoneConnected) {
                    updatedScModules.push('dataphoneConnected');
                }

                if (sc.cashdroConnected) {
                    updatedScModules.push('cashdroConnected');
                }

                return updatedScModules;
            }

            if (loggedUserData?.user?.lang) {
                // La cookie compartida con competitions manda sobre el idioma
                // del perfil: si no, al iniciar sesión aquí se desharía el
                // idioma que se acabara de elegir allí. Cuando no hay cookie se
                // siembra con el del perfil, para que ellos lo respeten.
                const sharedLocale = readLocaleCookie();
                const nextLanguage =
                    sharedLocale && isSupportedLanguage(sharedLocale)
                        ? getLanguageByKey(sharedLocale)
                        : getLanguageByKey(loggedUserData.user.lang);

                localStorage.setItem('lang', nextLanguage.key);
                writeLocaleCookie(nextLanguage.key);
                setLanguage(nextLanguage);
            }

            const newScData = { ...loggedUserData };
            if (loggedUserData?.sc?.id) {
                localStorage.setItem('idSC', `${loggedUserData.sc.id}`);
                newScData.sc = {
                    ...loggedUserData.sc,
                    modules: addFieldToModules(loggedUserData.sc),
                };
            }
            setUserData(newScData);
            setZero(loggedUserData?.sc?.zero || false);
        }
    }, [loggedUserData]);

    // Handle logged user errors
    useEffect(() => {
        if (loggedUserError) {
            const err: any = loggedUserError;

            // Check for PASSWORD_EXPIRED error
            if (
                isObject(err) &&
                isObject(err.error) &&
                Array.isArray(err.error.errors) &&
                isObject(err.error.errors[0]) &&
                err.error.errors?.[0]?.reason === 'PASSWORD_EXPIRED'
            ) {
                setLoggedUser(false);
                window.location.reload();
                return;
            }

            // Handle authentication errors (401, invalid token, etc.)
            // Clear the invalid token and redirect to login
            const status = err?.error?.status || err?.status;
            if (status === 401 || status === 403) {
                setLoggedUser(false);
                return;
            }

            // For any other /me error, also clear the session to prevent infinite loading
            // This handles cases where the token exists but is malformed or the user doesn't exist
            setLoggedUser(false);
        }
    }, [loggedUserError]);

    // Permisos v8 - Los roles del manager nuevo (sporttia-manager). Con el flag
    // `manager-roles-permissions` encendido se piden los permisos del operador
    // a `GET /v8/manager/me`; con el flag apagado (o si /flags falla) no se pide
    // nada y todo sigue como antes de los roles.
    const { data: flagsData, status: flagsStatus } = useQuery({
        ...flagsQueryOptions(request),
        enabled:
            migrationApis !== null &&
            userLogged &&
            v7DocInfoStatus === 'success',
    });

    const isSportcenterOperator = userData?.user?.role === 'SPORTCENTER';
    const rolesEnabled =
        userLogged &&
        isSportcenterOperator &&
        isFlagActive(flagsData, Flag.MANAGER_ROLES_PERMISSIONS);

    const { data: managerMeData, status: managerMeStatus } = useQuery({
        // Con el id del usuario en la clave, otro inicio de sesión (o una
        // suplantación) nunca ve los permisos del anterior.
        queryKey: [APPPROVIDER_MANAGER_ME, { userId: userData?.user?.id }],
        queryFn: () =>
            request<ManagerMe>('GET', '/manager/me', undefined, 'v8'),
        enabled: rolesEnabled,
        retry: 1,
    });

    const permissions =
        (rolesEnabled && managerMeData?.data?.permissions) || NO_PERMISSIONS;

    // Sync i18next language with AppContext language and update constants
    useEffect(() => {
        if (language?.key && i18n.language !== language.key) {
            i18n.changeLanguage(language.key);
            // Update date-fns locale to match the app language
            setDateLocale(language.key);
        }
        // Update constants when language changes
        setConstants(loadConstants(i18n.t));
    }, [language]);

    // For sportcenters, open socket conn. to the v7 back-end
    useEffect(() => {
        if (userData?.user?.role === 'SPORTCENTER') {
            if (typeof openSocket === 'function') {
                openSocket();
            }
        }
    }, [userData?.user?.role]);

    // Authentication//
    // If the user logged out, close the socket connection (if any).
    useEffect(() => {
        if (!userLogged) {
            if (typeof closeSocket === 'function') {
                closeSocket();
            }
        }
    }, [userLogged]);

    // Build status list for loader - only include loggedUserDataStatus if user is logged in
    // In React Query v5, disabled queries have status 'pending', which would cause infinite loading
    const statusListForLoader = [
        v7DocInfoStatus,
        translationsStatus,
        infoDataStatus,
        // Only include /me and /flags query status if user is logged in (queries are enabled)
        ...(userLogged ? [loggedUserDataStatus, flagsStatus] : []),
        // Only include /manager/me query status when roles are enabled (query is enabled)
        ...(rolesEnabled ? [managerMeStatus] : []),
    ];

    const [loading, loader] = useLoader(statusListForLoader);

    // Additional check: if user is logged but userData is not synced yet, keep loading
    // However, if there was an error fetching user data, don't keep loading (error handler will clear session)
    const hasUserDataError = loggedUserDataStatus === 'error';
    const isDataSynced =
        !userLogged || (userLogged && userData?.user?.id) || hasUserDataError;
    // useLoader va un render por detrás: sin esto las rutas llegarían a pintarse
    // una vez sin permisos justo cuando /me descubre que es un operador de centro.
    const arePermissionsPending = rolesEnabled && managerMeStatus === 'pending';
    const shouldShowLoader = loading || !isDataSynced || arePermissionsPending;

    const [header, setHeader] = useState<Header | null>(null);
    const [drawerOpen, setDrawerOpen] = useState<boolean>(true);
    const [zero, setZero] = useState<boolean>(false);

    // if (!infoData) {
    //     return null;
    // }

    const state: IAppContext = {
        migrationApis, // ApiDocV7//
        user: userData?.user, // CommonData//
        privileges: userData?.privileges, // CommonData//
        sc: userData?.sc, // CommonData//
        licence: userData?.licence, // CommonData//
        config: generalConfig, // CommonData//
        menus, // CommonData//
        apiEnv: window.runtimeConfig.apiEnv as APIEnv, // CommonData//
        backendVersion: infoData?.version, // CommonData//
        curtesyMinutesForTPV: infoData?.cortesyMinutesForTPV, // CommonData//
        constants, // CommonData//
        setLang: setLanguage, // Translations//
        lang: language, // Translations//
        t: translate, // Translations//
        api, // Api//
        getResource, // Api//
        showMessage, // AlertsUI//
        logged: userLogged, // Authentication//
        setLoggedUser, // Authentication//
        header, // App//
        setHeader, // App//
        drawerOpen, // Layout//
        setDrawerOpen, // Layout//
        zero, // Zero mode//
        setZero, // Zero mode//
        rolesEnabled, // Permissions//
        permissions, // Permissions//
        getSocketMessages: () => receivedMessages,
        deleteReceivedMessage,
        sendMessageToSocket: sendMessage,
    };

    return (
        <AppContext.Provider value={state}>
            {shouldShowLoader ? (
                loader
            ) : (
                <>
                    {children}
                    <ModalConfirmation
                        show={confirmationDialog.show}
                        onClose={() => setConfirmationDialog({ show: false })}
                        onAccept={confirmationDialog.onAccept}
                    />
                    {msg !== null && (
                        <Snackbar
                            open
                            autoHideDuration={msg.duration}
                            onClose={() => setMsg(null)}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'center',
                            }}
                            TransitionComponent={SlideTransition}
                            sx={{ mt: 2 }}
                        >
                            <Alert
                                elevation={0}
                                variant="standard"
                                onClose={() => setMsg(null)}
                                severity={msg.type}
                                icon={getAlertIcon(msg.type)}
                                sx={{
                                    minWidth: 360,
                                    maxWidth: 520,
                                    whiteSpace: 'pre-line',
                                    borderRadius: 3,
                                    backgroundColor: '#fff',
                                    boxShadow:
                                        '0 4px 20px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05)',
                                    fontSize: '0.9rem',
                                    fontWeight: 500,
                                    alignItems: 'center',
                                    py: 0,
                                    px: 0,
                                    overflow: 'hidden',
                                    '& .MuiAlert-icon': {
                                        fontSize: '1.4rem',
                                        mr: 1.5,
                                    },
                                    '& .MuiAlert-message': {
                                        color: '#333',
                                        py: 1.5,
                                        px: 2,
                                    },
                                    '& .MuiAlert-action': {
                                        paddingTop: 0,
                                        pr: 1,
                                    },
                                }}
                            >
                                <span
                                    onClick={() => {
                                        navigator.clipboard
                                            .writeText(msg.text)
                                            .then(() => {
                                                setMsg({
                                                    type: 'success',
                                                    text: i18n.t(
                                                        'common.copiedToClipboard',
                                                    ),
                                                    duration: 2000,
                                                });
                                            });
                                    }}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {msg.text}
                                </span>
                            </Alert>
                        </Snackbar>
                    )}
                </>
            )}
        </AppContext.Provider>
    );
}
